<template>
  <panel-navigation>
    <!-- Scan History Section -->
    <div class="listing-table-container">
      <!-- Section Title with Add Grid Button -->
      <div class="table-section-header">
        <div class="header-with-button">
          <div>
            <h2 class="table-section-title">📊 Scan History</h2>
            <p class="table-section-description">View and manage your recent grid scanning activity</p>
          </div>
          <button class="add-grid-btn" @click="openAddGridModal">
            <span class="btn-icon">➕</span>
            Add New Grid
          </button>
        </div>
      </div>

      <!-- Header Controls -->
      <div class="header-controls">
        <div class="search-filter-group">
          <div class="search-bar">
            <div class="search-icon">
              <svg viewBox="0 0 24 24" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
            <input type="text" placeholder="Search" @input="handleSearch">
          </div>
          <div class="filter-button">
            <svg viewBox="0 0 24 24" stroke-width="2">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"></polygon>
            </svg>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="table-container">
        <!-- Table Header -->
        <div class="table-header">
          <div class="table-cell location-cell">
            Grid Name
            <div class="sort-icon">
              <svg viewBox="0 0 24 24">
                <path d="M8 9l4-4 4 4M16 15l-4 4-4-4"/>
              </svg>
            </div>
          </div>
          <div class="table-cell search-query-cell">
            Keywords
            <div class="sort-icon">
              <svg viewBox="0 0 24 24">
                <path d="M8 9l4-4 4 4M16 15l-4 4-4-4"/>
              </svg>
            </div>
          </div>
          <div class="table-cell business-cell">
            Business Name
            <div class="sort-icon">
              <svg viewBox="0 0 24 24">
                <path d="M8 9l4-4 4 4M16 15l-4 4-4-4"/>
              </svg>
            </div>
          </div>
          <div class="table-cell search-settings-cell">
            Search Settings
            <div class="sort-icon">
              <svg viewBox="0 0 24 24">
                <path d="M8 9l4-4 4 4M16 15l-4 4-4-4"/>
              </svg>
            </div>
          </div>
          <div class="table-cell last-scan-cell">
                        
            Last Scan
            <div class="sort-icon">
              <svg viewBox="0 0 24 24">
                <path d="M8 9l4-4 4 4M16 15l-4 4-4-4"/>
              </svg>
            </div>
          </div>
          <div class="table-cell action-cell">
            Action
            <div class="sort-icon">
              <svg viewBox="0 0 24 24">
                <path d="M8 9l4-4 4 4M16 15l-4 4-4-4"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="table-row special-state">
          <p style="width: 100%; text-align: center;">Loading grid scans...</p>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="table-row special-state">
          <p style="width: 100%; text-align: center; color: #ef4444;">{{ error }}</p>
        </div>
        
        <!-- No Data State -->
        <div v-else-if="paginatedScans.length === 0" class="table-row special-state">
          <p style="width: 100%; text-align: center;">No grid scans found.</p>
        </div>
        
        <!-- Table Rows -->
        <template v-else>
          <div v-for="scan in paginatedScans" :key="scan.id" class="table-row">
            
          <div class="table-cell location-cell">{{ scan.grid_name || scan.business_name || 'N/A' }}</div>
          <div class="table-cell search-query-cell"><div class="keywords-container">
                <span 
                  v-for="(keyword, index) in getKeywordArray(scan.keywords)" 
                  :key="index" 
                  class="keyword-badge"
                >
                  {{ keyword }}
                </span>
                
              </div></div>
          <div class="table-cell business-cell">{{ scan.business_name || 'N/A' }}</div>
          <div class="table-cell search-settings-cell">
            <div class="grid-icon">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
            </div>
            <span>{{ formatGridSize(scan.grid_size) }}</span>
            <div class="miles-icon">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="8,12 12,16 16,12"/>
              </svg>
            </div>
            <span>{{ formatRadius(scan.radius, scan.distance_type) }}</span>
          </div>
          <div class="table-cell last-scan-cell">{{ formatDate(scan.last_run_at || scan.created_at) }}</div>
          <div class="table-cell action-cell">
            <button class="action-button" @click.stop="deleteGrid(scan.id)">
              🗑️ 
              Delete
            </button>
            <button class="action-button" @click="viewDetails(scan)">
              <svg viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              View Details
            </button>
          </div>
        </div>
        </template>
      </div>

      <!-- Pagination -->
      <div class="pagination" v-if="filteredScans.length > 0">
        <div class="pagination-left">
          <span>Show</span>
          <select v-model.number="itemsPerPage" @change="currentPage = 1" class="pagination-dropdown">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span>Entries</span>
        </div>
        <div class="pagination-right">
          <span>{{ paginationInfo }}</span>
          <div class="pagination-button" @click="previousPage" :style="{ opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }">
            <svg viewBox="0 0 24 24">
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
          </div>
          <div class="pagination-button" @click="nextPage" :style="{ opacity: currentPage >= totalPages ? 0.5 : 1, cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' }">
            <svg viewBox="0 0 24 24">
              <polyline points="9,18 15,12 9,6"></polyline>
            </svg>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Add New Grid Modal -->
    <div v-if="showAddGridModal" class="modal" @click="closeModalOnBackdrop($event, 'addGrid')">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ isEditMode ? '✏️ Edit Grid' : '➕ Add New Grid' }}</h3>
          <button class="close-modal" @click="closeAddGridModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Search for Your Business:</label>
            
            <!-- Search Provider Toggle -->
            <div class="search-provider-toggle" style="display: flex; align-items: center; margin-bottom: 12px; margin-top: 8px;">
              <label style="display: flex; align-items: center; cursor: pointer;">
                <input 
                  type="checkbox" 
                  v-model="addGrid.showValueSerpSearch"
                  @change="toggleSearchProvider('addGrid')"
                  style="display: none;"
                >
                <div class="toggle-switch" :class="{ 'active': addGrid.showValueSerpSearch }" 
                     style="position: relative; width: 48px; height: 24px; border-radius: 12px; transition: all 0.3s;"
                     :style="{ backgroundColor: addGrid.showValueSerpSearch ? '#4285f4' : '#ccc' }">
                  <div class="toggle-slider" 
                       style="position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; background-color: white; border-radius: 50%; transition: transform 0.3s; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" 
                       :style="{ transform: addGrid.showValueSerpSearch ? 'translateX(24px)' : 'translateX(0)' }"></div>
                </div>
                <span style="margin-left: 10px; font-size: 14px; color: #333;">Use Value SERP Search for SAB</span>
              </label>
            </div>
            <div v-if="addGrid.showValueSerpSearch" class="input-hint" style="margin-bottom: 10px; color: #666; font-size: 12px;">
              Can't find your business in Google? Value SERP provides extended search results
            </div>
            
            <!-- Google Places Search (Default) -->
            <div v-if="!addGrid.showValueSerpSearch">
              <input 
                ref="addGridBusinessSearch"
                v-model="addGrid.businessSearch"
                type="text" 
                placeholder="Search for your business name and location..." 
                style="width: 100%;"
              >
              <div class="input-hint">Start typing your business name to search using Google Places</div>
            </div>
            
            <!-- Value SERP Search with Autocomplete -->
            <div v-if="addGrid.showValueSerpSearch" style="position: relative;">
              <input 
                v-model="addGrid.valueSerpKeyword"
                @input="onValueSerpInput('addGrid')"
                @focus="addGrid.showValueSerpDropdown = true"
                @blur="hideValueSerpDropdown('addGrid')"
                class="form-control"
                placeholder="Type business name and location..."
                style="width: 100%; padding: 10px 12px; font-size: 14px; border: 1px solid #ddd; border-radius: 4px;"
                autocomplete="off"
              >
              
              <!-- Loading indicator inside input -->
              <div v-if="addGrid.valueSerpSearchLoading" style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%);">
                <i class="fa fa-spinner fa-spin" style="color: #999;"></i>
              </div>
              
              <!-- Loading state in dropdown -->
              <div v-if="addGrid.showValueSerpDropdown && addGrid.valueSerpSearchLoading && addGrid.valueSerpKeyword.length >= 3" 
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); padding: 20px; text-align: center; z-index: 1000;">
                <i class="fa fa-spinner fa-spin" style="color: #999; font-size: 20px;"></i>
                <div style="margin-top: 10px; color: #666; font-size: 13px;">Searching businesses...</div>
              </div>
              
              <!-- Autocomplete Dropdown -->
              <div v-if="addGrid.showValueSerpDropdown && !addGrid.valueSerpSearchLoading && addGrid.valueSerpSearchResults.length > 0" 
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-height: 300px; overflow-y: auto; z-index: 1000;">
                <div 
                  v-for="(result, index) in addGrid.valueSerpSearchResults" 
                  :key="result.data_cid || index"
                  @mousedown.prevent="selectValueSerpBusiness(result, 'addGrid')"
                  class="autocomplete-item"
                  style="padding: 10px 12px; cursor: pointer; border-bottom: 1px solid #f0f0f0; transition: background-color 0.2s;"
                  :style="{ backgroundColor: addGrid.hoveredIndex === index ? '#f5f5f5' : 'white' }"
                  @mouseenter="addGrid.hoveredIndex = index"
                  @mouseleave="addGrid.hoveredIndex = -1"
                >
                  <div style="font-size: 14px; color: #333; font-weight: 500; line-height: 1.4;">{{ result.title }}</div>
                  <div style="font-size: 12px; color: #666; margin-top: 2px; line-height: 1.3;">{{ result.address }}</div>
                </div>
              </div>
              
              <!-- No results message -->
              <div v-if="addGrid.showValueSerpDropdown && addGrid.valueSerpKeyword.length > 2 && !addGrid.valueSerpSearchLoading && addGrid.valueSerpSearchResults.length === 0"
                   style="position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #ddd; border-top: none; border-radius: 0 0 4px 4px; padding: 12px; text-align: center; color: #666; font-size: 13px;">
                No businesses found. Try a different search term.
              </div>
              
              <div class="input-hint mt-1">Search using Value SERP API • Results appear as you type</div>
            </div>
          </div>
          
          <div v-if="addGrid.selectedBusiness" class="form-row">
            <div class="form-group" style="position: relative;">
              <label>Selected Business:</label>
              <input 
                v-model="addGrid.selectedBusiness.name"
                type="text" 
                placeholder="Business name will appear here" 
                readonly 
                style="background-color: #f9fafb;"
              >
              <i v-if="addGrid.valueSerpDetailLoading" class="fa fa-spinner fa-spin" style="position: absolute; right: 10px; top: 38px; color: #999;"></i>
            </div>
            <div class="form-group" style="position: relative;">
              <label>Address:</label>
              <input 
                v-model="addGrid.selectedBusiness.address"
                type="text" 
                placeholder="Address will appear here" 
                readonly 
                style="background-color: #f9fafb;"
              >
              <i v-if="addGrid.valueSerpDetailLoading" class="fa fa-spinner fa-spin" style="position: absolute; right: 10px; top: 38px; color: #999;"></i>
            </div>
          </div>
          
          <div class="form-group">
            <label>Keywords:</label>
            <div class="keywords-input-container">
              <div class="keywords-tag-container">
                <div class="keywords-tag-wrapper">
                  <div 
                    v-for="(keyword, index) in addGrid.keywords" 
                    :key="index" 
                    class="keyword-tag-item"
                  >
                    <span 
                      class="keyword-tag-text" 
                      contenteditable="true"
                      @blur="editAddGridKeyword(index, $event)"
                      @keypress.enter.prevent="$event.target.blur()"
                    >{{ keyword }}</span>
                    <button class="keyword-tag-delete" @click="removeAddGridKeyword(index)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input 
                    v-model="addGrid.keywordInput"
                    @keypress.enter.prevent="addAddGridKeyword"
                    @input="handleAddGridKeywordInput"
                    type="text" 
                    class="keyword-tag-input" 
                    placeholder="Type a keyword and press Enter..."
                  >
                </div>
              </div>
              <div class="input-hint">Press Enter to add keywords • Click to edit • Click × to delete</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Grid Size:</label>
              <select v-model="addGrid.gridSize" class="form-select" @change="updateAddGridPreview">
                <option value="3x3">3 x 3 (9 locations)</option>
                <option value="5x5">5 x 5 (25 locations)</option>
                <option value="7x7">7 x 7 (49 locations)</option>
                <option value="10x10">10 x 10 (100 locations)</option>
              </select>
            </div>
            <div class="form-group">
              <label>Radius:</label>
              <select v-model="addGrid.radius" class="form-select" @change="updateAddGridPreview">
                <option value="0.5">0.5 Mile</option>
                <option value="1">1 Mile</option>
                <option value="2">2 Miles</option>
                <option value="5">5 Miles</option>
                <option value="10">10 Miles</option>
              </select>
            </div>
          </div>
          
          <!-- Grid Preview Map -->
          <div class="form-group">
            <label>Grid Coverage Preview:</label>
            <div class="grid-preview-container">
              <div class="grid-preview-map" style="position: relative;">
                <div ref="addGridGoogleMap" style="width: 100%; height: 300px;"></div>
                <div class="grid-info-badge" style="position: absolute; top: 10px; left: 50%; transform: translateX(-50%); z-index: 100;">
                  <span>{{ addGrid.gridSize }} Grid • {{ addGrid.radius }} Mile Radius</span>
                </div>
              </div>
              <div class="map-hint">Search for your business above • Drag the grid to adjust position</div>
            </div>
          </div>

          <div class="form-group">
            <label>Competitors (Optional):</label>
            <div class="competitors-input-container">
              <input 
                ref="addGridCompetitorSearch"
                v-model="addGrid.competitorSearch"
                type="text" 
                class="competitor-search-input" 
                placeholder="Search for competitor businesses..."
              >
              <div v-if="addGrid.competitors.length > 0" class="competitors-tag-container" style="margin-top: 8px;">
                <div class="competitors-tag-wrapper">
                  <div 
                    v-for="(competitor, index) in addGrid.competitors" 
                    :key="index" 
                    class="competitor-tag-item"
                  >
                    <span class="competitor-tag-text" :title="competitor.address">{{ competitor.name }}</span>
                    <button class="competitor-tag-delete" @click="removeAddGridCompetitor(index)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div class="input-hint">Search and select competitor businesses • Click × to remove</div>
            </div>
          </div>

          <div class="form-group">
            <label>Grid Name (Optional):</label>
            <input v-model="addGrid.gridName" type="text" placeholder="e.g., Downtown Area Tracking">
          </div>
          
          <!-- Scheduling Options -->
          <div class="scheduling-section">
            <h4 class="section-title">📅 Scheduling Options</h4>
            <div class="form-row">
              <div class="form-group">
                <label>Scan Frequency:</label>
                <select v-model="addGrid.scanFrequency" class="form-select">
                  <option value="manual">Manual Only</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div class="form-group">
                <label>Preferred Time:</label>
                <select v-model="addGrid.scanTime" class="form-select">
                  <option value="morning">Morning (9:00 AM)</option>
                  <option value="afternoon">Afternoon (2:00 PM)</option>
                  <option value="evening">Evening (6:00 PM)</option>
                  <option value="night">Night (11:00 PM)</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label>Email Recipients:</label>
              <div class="email-tags-container">
                <div class="email-tags-wrapper">
                  <div 
                    v-for="(email, index) in addGrid.emailRecipients" 
                    :key="index" 
                    class="email-tag-item"
                    :class="{ invalid: !isValidEmail(email) }"
                  >
                    <span class="email-tag-text" :title="email">{{ email }}</span>
                    <button class="email-tag-delete" @click="removeEmailRecipient(index)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <input 
                    v-model="addGrid.emailInput"
                    @keypress.enter.prevent="addEmailRecipient"
                    @input="handleEmailInput"
                    type="email" 
                    class="email-tag-input" 
                    placeholder="Enter email address and press Enter..."
                  >
                </div>
              </div>
              <div class="input-hint">Press Enter to add email addresses • Click × to delete</div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>Report Format:</label>
                <select v-model="addGrid.reportFormat" class="form-select">
                  <option value="pdf">PDF Report</option>
                  <option value="csv">CSV Data</option>
                  <option value="xlsx">Excel Spreadsheet</option>
                  <option value="both">Both PDF & CSV</option>
                </select>
              </div>
              <div class="form-group">
                <label>Report Name:</label>
                <input v-model="addGrid.reportName" type="text" placeholder="Weekly Ranking Report">
              </div>
            </div>
            <div class="form-group">
              <label>Email Notifications:</label>
              <div class="notification-options">
                <label class="checkbox-label">
                  <input v-model="addGrid.emailOnComplete" type="checkbox">
                  <span class="checkmark"></span>
                  Send email when scan completes
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="action-btn secondary" @click="closeAddGridModal">Cancel</button>
          <button class="action-btn primary" @click="saveNewGrid">{{ isEditMode ? 'Update Grid' : 'Save Grid' }}</button>
        </div>
      </div>
    </div>
  </panel-navigation>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'
import $ from 'jquery'
import moment from 'moment'
import { GetSuggestions } from '@/utils/PlaceUtils'
import PanelNavigation from '@/components/PanelNavigation.vue'
import debounce from 'lodash/debounce'

export default {
    metaInfo () {
    return {
      title: "Grid Scans | " + this.$appName
    }
  },
  components: {
    PanelNavigation
  },
  data() {
    return {
      gridScans: [],
      isLoading: false,
      error: null,
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 10,
      
      // Add Grid Modal properties
      showAddGridModal: false,
      isEditMode: false,
      editingGridId: null,
      googleMapsLoaded: false,
      
      // Add Grid data
      addGrid: {
        keywords: [],
        keywordInput: '',
        businessSearch: '',
        selectedBusiness: null,
        gridSize: '3x3',
        radius: '1',
        competitors: [],
        competitorSearch: '',
        gridName: '',
        scanFrequency: 'daily',
        scanTime: 'afternoon',
        emailRecipients: [],
        emailInput: '',
        reportFormat: 'pdf',
        reportName: 'Weekly Ranking Report',
        emailOnComplete: true,
        emailOnChanges: false,
        weeklyReports: false,
        gridCenter: null,
        gridPoints: [],
        // Value SERP properties
        showValueSerpSearch: false,
        valueSerpKeyword: '',
        valueSerpSearchResults: [],
        valueSerpSearchLoading: false,
        valueSerpDetailLoading: false,
        showValueSerpDropdown: false,
        hoveredIndex: -1,
        searchProvider: 'google_places'
      },
      
      // Google Maps references
      addGridMap: null,
      addGridAutocomplete: null,
      addGridCompetitorAutocomplete: null,
      addGridOverlay: null,
      addGridMarkers: [],
      addGridCenterMarker: null
    }
  },
  computed: {
    ...mapGetters('apiKeys', [
      'isGoogleMapsConfigured',
      'isGoogleMapsValid',
      'isValueSerpConfigured',
      'isValueSerpValid'
    ]),
    
    ...mapGetters('auth', [
      'hasActiveSubscription',
      'subscriptionStatus'
    ]),
    
    filteredScans() {
      if (!this.searchQuery) {
        return this.gridScans;
      }
      const query = this.searchQuery.toLowerCase();
      return this.gridScans.filter(scan => 
        (scan.grid_name && scan.grid_name.toLowerCase().includes(query)) ||
        (scan.business_name && scan.business_name.toLowerCase().includes(query)) ||
        (scan.keywords && scan.keywords.toLowerCase().includes(query))
      );
    },
    
    paginatedScans() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredScans.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredScans.length / this.itemsPerPage);
    },
    
    paginationInfo() {
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.currentPage * this.itemsPerPage, this.filteredScans.length);
      return `${start} - ${end} of ${this.filteredScans.length}`;
    }
  },
  methods: {
    async fetchGridScans() {
      this.isLoading = true;
      this.error = null;
      
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scans`, {
          headers: {
            Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
            "Content-Type": "application/json"
          }
        });
        
        if (response.data.success) {
          this.gridScans = response.data.data || [];
        }
      } catch (error) {
        console.error('Error fetching grid scans:', error);
        this.error = 'Failed to fetch grid scans. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Never';
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);
      
      if (hours < 1) return 'Just now';
      if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString();
    },
    
    formatKeywords(keywords) {
      if (!keywords) return '';
      // If it's a JSON string, parse it
      if (typeof keywords === 'string') {
        try {
          const parsed = JSON.parse(keywords);
          return Array.isArray(parsed) ? parsed.join(', ') : keywords;
        } catch {
          return keywords;
        }
      }
      return Array.isArray(keywords) ? keywords.join(', ') : keywords;
    },
    
    formatGridSize(gridSize) {
      return gridSize || 'N/A';
    },
    
    formatRadius(radius, distanceType) {
      if (!radius) return 'N/A';
      const unit = distanceType === 'Kilometers' ? 'Kilometer' : 'Mile';
      return `${radius} ${unit}${radius !== 1 ? 's' : ''}`;
    },
    
    viewDetails(scan) {
      const routeData = this.$router.resolve({
        name: 'GridScanDetails',
        params: { id: scan.id }
      });
      window.open(routeData.href, '_blank');
    },
    
    scanAgain(scan) {
      // Implement scan again functionality
      console.log('Scan again:', scan);
    },
    
    handleSearch(event) {
      this.searchQuery = event.target.value;
      this.currentPage = 1;
    },
    
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
        async deleteGrid(gridId) {
      const grid = this.gridScans.find(g => g.id === gridId);
      
      const result = await this.$swal({
        title: 'Confirm Delete',
        text: `Are you sure you want to delete "${grid.grid_name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      });
      
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/delete`, {
            data: { scanIds: [gridId] }, // Send as array even for single delete
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          });
          
          if (response.data.success) {
            // Track grid deletion
            this.$posthog.capture('grid_deleted', {
              grid_id: gridId,
              grid_name: grid.grid_name,
              total_scans: grid.total_runs || 0,
              created_days_ago: grid.created_at ? Math.floor((Date.now() - new Date(grid.created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0
            });
            
            this.$swal({
              icon: 'success',
              title: 'Deleted!',
              text: `Grid scan "${grid.grid_name}" has been deleted.`,
              timer: 2000,
              showConfirmButton: false
            });
            
            // Refresh the grid scans list
            await this.fetchUserGridScans();
          } else {
            throw new Error(response.data.message);
          }
        } catch (error) {
          console.error('Error deleting grid scan:', error);
          this.$swal({
            icon: 'error',
            title: 'Error',
            text: (error.response && error.response.data && error.response.data.message) || error.message || 'Failed to delete grid scan'
          });
        }
      }
    },
        getKeywordArray(keywords) {
      // If already an array, return it
      if (Array.isArray(keywords)) {
        return keywords;
      }
      
      // If it's a JSON string, parse it
      if (typeof keywords === 'string') {
        try {
          const parsed = JSON.parse(keywords);
          return Array.isArray(parsed) ? parsed : [keywords];
        } catch (error) {
          // If it's a comma-separated string, split it
          if (keywords.includes(',')) {
            return keywords.split(',').map(k => k.trim());
          }
          // Otherwise return as single-item array
          return [keywords];
        }
      }
      
      return [];
    },
    
    // Value SERP Methods
    toggleSearchProvider(modalType) {
      const scanData = modalType === 'addGrid' ? this.addGrid : null;
      if (!scanData) return;
      
      // Update search provider based on toggle state
      if (scanData.showValueSerpSearch) {
        this.switchToValueSerp(modalType);
      } else {
        this.switchToGooglePlaces(modalType);
      }
    },
    
    switchToGooglePlaces(modalType) {
      const scanData = modalType === 'addGrid' ? this.addGrid : null;
      if (!scanData) return;
      
      // Check if Google Maps API key is valid
      if (!this.isGoogleMapsValid) {
        this.$swal({
          icon: 'warning',
          title: 'Google Maps API Key Required',
          text: !this.isGoogleMapsConfigured 
            ? 'Please configure your Google Maps API key in Settings to use Google Places.' 
            : 'Your Google Maps API key is invalid. Please check your API key in Settings.',
          confirmButtonText: 'Go to Settings',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.push('/user-settings');
          }
        });
        // Reset toggle back
        scanData.showValueSerpSearch = true;
        return;
      }
      
      scanData.searchProvider = 'google_places';
      scanData.valueSerpKeyword = '';
      scanData.valueSerpSearchResults = [];
      scanData.selectedBusiness = null;
      
      // Re-initialize Google Places autocomplete
      this.$nextTick(() => {
        if (this.$refs.addGridBusinessSearch) {
          this.initAddGridAutocomplete();
        }
      });
    },
    
    switchToValueSerp(modalType) {
      const scanData = modalType === 'addGrid' ? this.addGrid : null;
      if (!scanData) return;
      
      // Check if Value SERP API key is valid
      if (!this.isValueSerpValid) {
        this.$swal({
          icon: 'warning',
          title: 'Value SERP API Key Required',
          text: !this.isValueSerpConfigured 
            ? 'Please configure your Value SERP API key in Settings to use this feature.' 
            : 'Your Value SERP API key is invalid. Please check your API key in Settings.',
          confirmButtonText: 'Go to Settings',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.push('/user-settings');
          }
        });
        // Reset toggle back
        scanData.showValueSerpSearch = false;
        return;
      }
      
      scanData.searchProvider = 'value_serp';
      scanData.businessSearch = '';
      scanData.selectedBusiness = null;
    },
    
    onValueSerpInput(modalType) {
      const scanData = modalType === 'addGrid' ? this.addGrid : null;
      if (!scanData) return;
      
      // Only search if input has at least 3 characters
      if (scanData.valueSerpKeyword.length >= 3) {
        this.debouncedValueSerpSearch(modalType);
      } else {
        scanData.valueSerpSearchResults = [];
        scanData.showValueSerpDropdown = false;
      }
    },
    
    debouncedValueSerpSearch: debounce(async function(modalType) {
      await this.performValueSerpSearch(modalType);
    }, 300),
    
    async performValueSerpSearch(modalType) {
      const scanData = modalType === 'addGrid' ? this.addGrid : null;
      if (!scanData) return;
      
      if (!scanData.valueSerpKeyword || scanData.valueSerpKeyword.length < 3) {
        scanData.valueSerpSearchResults = [];
        return;
      }
      
      scanData.valueSerpSearchLoading = true;
      scanData.showValueSerpDropdown = true;
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/value-serp/search`,
          { 
            keyword: scanData.valueSerpKeyword
          },
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );
        
        if (response.data.success && response.data.suggestions) {
          scanData.valueSerpSearchResults = response.data.suggestions.slice(0, 10);
        }
      } catch (error) {
        console.error('Value SERP search error:', error);
        scanData.valueSerpSearchResults = [];
      } finally {
        scanData.valueSerpSearchLoading = false;
      }
    },
    
    hideValueSerpDropdown(modalType) {
      const scanData = modalType === 'addGrid' ? this.addGrid : null;
      if (!scanData) return;
      
      // Delay hiding to allow click events to process
      setTimeout(() => {
        scanData.showValueSerpDropdown = false;
        scanData.hoveredIndex = -1;
      }, 200);
    },
    
    async selectValueSerpBusiness(business, modalType) {
      const scanData = modalType === 'addGrid' ? this.addGrid : null;
      if (!scanData) return;
      
      // Close dropdown immediately
      scanData.showValueSerpDropdown = false;
      scanData.hoveredIndex = -1;
      
      // Show loading state for business details
      scanData.valueSerpDetailLoading = true;
      
      // Set temporary placeholder while loading
      scanData.selectedBusiness = {
        name: 'Loading...',
        address: 'Fetching business details...',
        placeId: null
      };
      
      try {
        // Get full place details with coordinates
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/value-serp/place-details`,
          { 
            data_cid: business.data_cid 
          },
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`
            }
          }
        );
        
        if (response.data.success && response.data.data) {
          const placeData = response.data.data;
          // Format the business data similar to Google Places
          const formattedBusiness = {
            name: placeData.title,
            address: placeData.address,
            formatted_address: placeData.address,
            placeId: placeData.place_id,
            data_cid: business.data_cid,
            location: {
              lat: parseFloat(placeData.latitude),
              lng: parseFloat(placeData.longitude)
            },
            source: 'value_serp'
          };
          
          // Update the selected business
          scanData.selectedBusiness = formattedBusiness;
          
          // Set grid center for map
          scanData.gridCenter = {
            lat: parseFloat(placeData.latitude),
            lng: parseFloat(placeData.longitude)
          };
          
          // Update input field
          scanData.valueSerpKeyword = placeData.title;
          
          // Update the map and draw grid preview
          if (this.addGridMap) {
            const center = new window.google.maps.LatLng(
              parseFloat(placeData.latitude),
              parseFloat(placeData.longitude)
            );
            this.addGridMap.setCenter(center);
            this.addGridMap.setZoom(14);
            
            // Draw the grid preview
            this.drawAddGridPreview();
          }
        } else {
          throw new Error('Failed to get place details');
        }
      } catch (error) {
        console.error('Error fetching Value SERP place details:', error);
        
        // Reset selected business on error
        scanData.selectedBusiness = null;
        
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to get business details. Please try again.'
        });
      } finally {
        scanData.valueSerpDetailLoading = false;
      }
    },
    
    // Add Grid Modal Methods
    async openAddGridModal() {
      // Check if API key is valid
      if (!this.isGoogleMapsValid) {
        this.$swal({
          icon: 'warning',
          title: 'Google Maps API Key Required',
          text: !this.isGoogleMapsConfigured 
            ? 'Please configure your Google Maps API key in Settings to use Grid Scan.' 
            : 'Your Google Maps API key is invalid. Please check your API key in Settings.',
          confirmButtonText: 'Go to Settings',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.isConfirmed) {
            this.$router.push('/user-settings');
          }
        });
        return;
      }
      
      this.showAddGridModal = true;
      await this.$nextTick();
      
      if (!this.googleMapsLoaded) {
        await this.loadGoogleMapsAPI();
      }
      
      setTimeout(() => {
        this.initAddGridMap();
        // Only initialize Google Places autocomplete if using Google Places
        if (!this.addGrid.showValueSerpSearch) {
          this.initAddGridAutocomplete();
          this.initAddGridCompetitorAutocomplete();
        }
      }, 100);
    },
    
    closeAddGridModal() {
      this.showAddGridModal = false;
      this.resetAddGridForm();
      this.isEditMode = false;
      this.editingGridId = null;
    },
    
    resetAddGridForm() {
      this.addGrid = {
        keywords: [],
        keywordInput: '',
        businessSearch: '',
        selectedBusiness: null,
        gridSize: '3x3',
        radius: '1',
        competitors: [],
        competitorSearch: '',
        gridName: '',
        scanFrequency: 'daily',
        scanTime: 'afternoon',
        emailRecipients: [],
        emailInput: '',
        reportFormat: 'pdf',
        reportName: 'Weekly Ranking Report',
        emailOnComplete: true,
        emailOnChanges: false,
        weeklyReports: false,
        gridCenter: null,
        gridPoints: [],
        // Value SERP properties
        showValueSerpSearch: false,
        valueSerpKeyword: '',
        valueSerpSearchResults: [],
        valueSerpSearchLoading: false,
        valueSerpDetailLoading: false,
        showValueSerpDropdown: false,
        hoveredIndex: -1,
        searchProvider: 'google_places'
      };
    },
    
    // Google Maps initialization
    async loadGoogleMapsAPI() {
      if (window.google && window.google.maps) {
        this.googleMapsLoaded = true;
        return;
      }
      
      // Get the user's API key from Vuex store
      const userApiKey = this.$store.getters['apiKeys/googleMapsKey'];
      
      if (!userApiKey) {
        console.error('No Google Maps API key found');
        return Promise.reject('No API key configured');
      }
      
      return new Promise((resolve, reject) => {
        window.initGoogleMaps = () => {
          this.googleMapsLoaded = true;
          resolve();
        };
        
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${userApiKey}&libraries=geometry,places&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    initAddGridMap() {
      if (!this.$refs.addGridGoogleMap || !window.google) return;
      
      const mapCenter = this.addGrid.gridCenter || { lat: 33.5186, lng: -86.8104 };
      
      this.addGridMap = new window.google.maps.Map(this.$refs.addGridGoogleMap, {
        zoom: 14,
        center: mapCenter,
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        clickableIcons: false
      });
      
      if (this.addGrid.gridCenter) {
        this.drawAddGridPreview();
      }
    },
    
    initAddGridAutocomplete() {
      if (!this.$refs.addGridBusinessSearch || !window.google) return;
      
      this.addGridAutocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.addGridBusinessSearch,
        {
          types: ['establishment'],
          fields: ['place_id', 'name', 'formatted_address', 'geometry', 'types']
        }
      );
      
      this.addGridAutocomplete.addListener('place_changed', () => {
        const place = this.addGridAutocomplete.getPlace();
        
        if (!place.geometry || !place.geometry.location) {
          alert('No location found for this place. Please select from the dropdown.');
          return;
        }
        
        this.addGrid.selectedBusiness = {
          name: place.name || '',
          address: place.formatted_address || '',
          placeId: place.place_id,
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }
        };
        
        this.addGrid.gridCenter = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        
        if (this.addGridMap) {
          this.addGridMap.setCenter(place.geometry.location);
          this.drawAddGridPreview();
        }
      });
    },
    
    initAddGridCompetitorAutocomplete() {
      if (!this.$refs.addGridCompetitorSearch || !window.google) return;
      
      this.addGridCompetitorAutocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.addGridCompetitorSearch,
        {
          types: ['establishment'],
          fields: ['place_id', 'name', 'formatted_address', 'geometry']
        }
      );
      
      this.addGridCompetitorAutocomplete.addListener('place_changed', () => {
        const place = this.addGridCompetitorAutocomplete.getPlace();
        
        if (!place.geometry || !place.geometry.location) {
          return;
        }
        
        const exists = this.addGrid.competitors.some(comp => comp.place_id === place.place_id);
        if (exists) {
          alert('This competitor is already in your list.');
          this.addGrid.competitorSearch = '';
          return;
        }
        
        this.addGrid.competitors.push({
          place_id: place.place_id,
          name: place.name || 'Unknown Business',
          address: place.formatted_address || '',
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }
        });
        
        this.addGrid.competitorSearch = '';
      });
    },
    
    drawAddGridPreview() {
      if (!this.addGridMap || !this.addGrid.gridCenter) return;
      
      // Clear existing grid
      this.clearAddGridPreview();
      
      const gridSize = this.addGrid.gridSize;
      const radius = parseFloat(this.addGrid.radius);
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n));
      
      // Convert miles to meters
      const radiusInMeters = radius * 1609.34;
      
      // Calculate grid spacing
      const totalWidth = radiusInMeters * 2;
      const totalHeight = radiusInMeters * 2;
      const cellWidth = totalWidth / (cols - 1);
      const cellHeight = totalHeight / (rows - 1);
      
      // Create bounds for the grid
      const bounds = new window.google.maps.LatLngBounds();
      const gridPoints = [];
      
      // Calculate grid points
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const offsetX = (col - (cols - 1) / 2) * cellWidth;
          const offsetY = ((rows - 1) / 2 - row) * cellHeight;
          
          const point = window.google.maps.geometry.spherical.computeOffset(
            new window.google.maps.LatLng(this.addGrid.gridCenter.lat, this.addGrid.gridCenter.lng),
            Math.sqrt(offsetX * offsetX + offsetY * offsetY),
            Math.atan2(offsetX, offsetY) * 180 / Math.PI
          );
          
          gridPoints.push(point);
          bounds.extend(point);
        }
      }
      
      // Create blue background as a Rectangle
      this.addGridOverlay = new window.google.maps.Rectangle({
        bounds: bounds,
        map: this.addGridMap,
        fillColor: '#4f46e5',
        fillOpacity: 0.15,
        strokeColor: '#4f46e5',
        strokeOpacity: 0.3,
        strokeWeight: 2,
        draggable: false,
        geodesic: false,
        zIndex: 1
      });
      
      // Create grid point markers
      let markerIndex = 1;
      gridPoints.forEach((point) => {
        const marker = new window.google.maps.Marker({
          position: point,
          map: this.addGridMap,
          label: {
            text: '?',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 'bold'
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#4f46e5',
            fillOpacity: 0.8,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            scale: 15
          },
          title: `Grid Point ${markerIndex} - Rank Unknown`,
          zIndex: 1000
        });
        
        this.addGridMarkers.push(marker);
        markerIndex++;
      });
      
      // Add center marker
      if (this.addGridCenterMarker) {
        this.addGridCenterMarker.setMap(null);
      }
      
      this.addGridCenterMarker = new window.google.maps.Marker({
        position: new window.google.maps.LatLng(this.addGrid.gridCenter.lat, this.addGrid.gridCenter.lng),
        map: this.addGridMap,
        title: 'Drag to move grid',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
            '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
            '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" fill="#ef4444" stroke="#FFFFFF" stroke-width="2"/>' +
            '<circle cx="12" cy="10" r="3" fill="#FFFFFF"/>' +
            '</svg>'
          ),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 40)
        },
        draggable: true,
        cursor: 'move',
        zIndex: 1001
      });
      
      // Add drag listeners
      this.addGridCenterMarker.addListener('drag', (event) => {
        this.addGrid.gridCenter = { 
          lat: event.latLng.lat(), 
          lng: event.latLng.lng() 
        };
        this.updateAddGridWhileDragging();
      });
      
      this.addGridCenterMarker.addListener('dragend', (event) => {
        this.addGrid.gridCenter = { 
          lat: event.latLng.lat(), 
          lng: event.latLng.lng() 
        };
        this.drawAddGridPreview();
      });
      
      // Adjust map to show entire grid
      this.addGridMap.fitBounds(bounds);
      const currentZoom = this.addGridMap.getZoom();
      if (currentZoom > 15) {
        this.addGridMap.setZoom(15);
      }
    },
    
    updateAddGridWhileDragging() {
      if (!this.addGridMap || !this.addGrid.gridCenter) return;
      
      const gridSize = this.addGrid.gridSize;
      const radius = parseFloat(this.addGrid.radius);
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n));
      
      const radiusInMeters = radius * 1609.34;
      const totalWidth = radiusInMeters * 2;
      const totalHeight = radiusInMeters * 2;
      const cellWidth = totalWidth / (cols - 1);
      const cellHeight = totalHeight / (rows - 1);
      
      const bounds = new window.google.maps.LatLngBounds();
      
      let markerIndex = 0;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          if (markerIndex < this.addGridMarkers.length) {
            const offsetX = (col - (cols - 1) / 2) * cellWidth;
            const offsetY = ((rows - 1) / 2 - row) * cellHeight;
            
            const point = window.google.maps.geometry.spherical.computeOffset(
              new window.google.maps.LatLng(this.addGrid.gridCenter.lat, this.addGrid.gridCenter.lng),
              Math.sqrt(offsetX * offsetX + offsetY * offsetY),
              Math.atan2(offsetX, offsetY) * 180 / Math.PI
            );
            
            this.addGridMarkers[markerIndex].setPosition(point);
            bounds.extend(point);
            markerIndex++;
          }
        }
      }
      
      if (this.addGridOverlay) {
        this.addGridOverlay.setBounds(bounds);
      }
    },
    
    clearAddGridPreview() {
      // Clear markers
      this.addGridMarkers.forEach(marker => {
        marker.setMap(null);
      });
      this.addGridMarkers = [];
      
      // Clear overlay
      if (this.addGridOverlay) {
        this.addGridOverlay.setMap(null);
      }
      
      // Clear center marker
      if (this.addGridCenterMarker) {
        this.addGridCenterMarker.setMap(null);
      }
    },
    
    updateAddGridPreview() {
      if (this.addGridMap && this.addGrid.gridCenter) {
        this.drawAddGridPreview();
      }
    },
    
    // Keyword management for Add Grid
    addAddGridKeyword() {
      const keyword = this.addGrid.keywordInput.trim();
      if (keyword && !this.addGrid.keywords.includes(keyword)) {
        this.addGrid.keywords.push(keyword);
        this.addGrid.keywordInput = '';
      }
    },
    
    removeAddGridKeyword(index) {
      this.addGrid.keywords.splice(index, 1);
    },
    
    editAddGridKeyword(index, event) {
      const newValue = event.target.textContent.trim();
      if (newValue) {
        this.addGrid.keywords[index] = newValue;
      } else {
        this.removeAddGridKeyword(index);
      }
    },
    
    handleAddGridKeywordInput(event) {
      // Handle any special input processing if needed
    },
    
    // Competitor management
    removeAddGridCompetitor(index) {
      this.addGrid.competitors.splice(index, 1);
    },
    
    // Email management
    addEmailRecipient() {
      const email = this.addGrid.emailInput.trim();
      if (email && this.isValidEmail(email) && !this.addGrid.emailRecipients.includes(email)) {
        this.addGrid.emailRecipients.push(email);
        this.addGrid.emailInput = '';
      }
    },
    
    removeEmailRecipient(index) {
      this.addGrid.emailRecipients.splice(index, 1);
    },
    
    handleEmailInput(event) {
      // Handle any special input processing if needed
    },
    
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    
    // Save the grid
    async saveNewGrid() {
      // Validate required fields
      if (!this.addGrid.selectedBusiness) {
        this.$swal({
          icon: 'error',
          title: 'Missing Business',
          text: 'Please search and select your business.'
        });
        return;
      }
      
      if (this.addGrid.keywords.length === 0) {
        this.$swal({
          icon: 'error',
          title: 'Missing Keywords',
          text: 'Please add at least one keyword to track.'
        });
        return;
      }
      
      // Convert scan time to HH:MM format
      let scanTimeFormatted = null; // Default to null for manual
      
      // Only process scan time if not manual frequency
      if (this.addGrid.scanFrequency !== 'manual') {
        // Map time options to actual times
        const timeMapping = {
          'morning': '09:00',
          'afternoon': '14:00',
          'evening': '18:00',
          'night': '23:00'
        };
        
        // If scanTime is in the format above, map it, otherwise use as is
        if (timeMapping[this.addGrid.scanTime]) {
          scanTimeFormatted = timeMapping[this.addGrid.scanTime];
        } else if (this.addGrid.scanTime && this.addGrid.scanTime.includes(':')) {
          // If already in HH:MM format
          scanTimeFormatted = this.addGrid.scanTime;
        } else {
          scanTimeFormatted = '09:00'; // Default to 9 AM if not set
        }
      }
      
      // Generate grid points if not already generated
      if (!this.addGrid.gridPoints || this.addGrid.gridPoints.length === 0) {
        await this.generateGridPoints();
      }
      
      // Prepare data for API - matching Agency.vue structure exactly
      const gridScanData = {
        // Business Information
        business_name: this.addGrid.selectedBusiness.name,
        business: this.addGrid.selectedBusiness,
        address: this.addGrid.selectedBusiness.address,
        latitude: (this.addGrid.gridCenter && this.addGrid.gridCenter.lat) || (this.addGrid.selectedBusiness.location && this.addGrid.selectedBusiness.location.lat) || this.addGrid.selectedBusiness.latitude,
        longitude: (this.addGrid.gridCenter && this.addGrid.gridCenter.lng) || (this.addGrid.selectedBusiness.location && this.addGrid.selectedBusiness.location.lng) || this.addGrid.selectedBusiness.longitude,
        
        // Grid Configuration
        grid_name: this.addGrid.gridName,
        keywords: this.addGrid.keywords,
        grid_size: this.addGrid.gridSize,
        radius: this.addGrid.radius,
        distance_type: 'miles',
        grid_points: this.addGrid.gridPoints || [],
        
        // Scheduling Information
        scan_frequency: this.addGrid.scanFrequency,
        scan_time: scanTimeFormatted,
        notification_email: this.addGrid.emailRecipients.join(','),
        
        // User timezone
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        
        // Search provider (google_places or value_serp)
        search_provider: this.addGrid.searchProvider,
        
        // Additional metadata
        competitors: this.addGrid.competitors,
        report_format: this.addGrid.reportFormat,
        report_name: this.addGrid.reportName,
        email_on_complete: this.addGrid.emailOnComplete,
        email_on_changes: this.addGrid.emailOnChanges,
        weekly_reports: this.addGrid.weeklyReports,
        
        // Flag to trigger immediate scan for manual frequency
        trigger_immediately: this.addGrid.scanFrequency === 'manual'
      };
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan`,
          gridScanData,
          {
            headers: {
              Authorization: `Bearer ${(this.$store.getters['auth/jwt'] || this.$store.getters['auth/accessToken'])}`,
              "Content-Type": "application/json"
            }
          }
        );
        
        if (response.data.success) {
          // Show success message based on frequency type
          let successMessage;
          if (this.addGrid.scanFrequency === 'manual') {
            successMessage = `Your grid scan "${this.addGrid.gridName}" has been created and is running now! You can trigger it manually anytime using "Scan Again".`;
          } else {
            successMessage = `Your grid scan "${this.addGrid.gridName}" has been scheduled successfully. Next run: ${new Date(response.data.next_run).toLocaleString()}`;
          }
          
          this.$swal({
            icon: 'success',
            title: 'Grid Scan Created!',
            html: `<div style="text-align: left;">
              <p>${successMessage}</p>
            </div>`,
            showConfirmButton: true,
            confirmButtonText: 'Got it!',
            timer: 5000
          });
          
          this.closeAddGridModal();
          this.fetchGridScans(); // Refresh the list
        }
      } catch (error) {
        console.error('Error creating grid:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.error) || 'Failed to create grid scan. Please try again.'
        });
      }
    },
    
    // Generate grid points for the scan
    async generateGridPoints() {
      if (!this.addGrid.selectedBusiness) return;
      
      const center = this.addGrid.gridCenter || {
        lat: (this.addGrid.selectedBusiness.location && this.addGrid.selectedBusiness.location.lat) || this.addGrid.selectedBusiness.latitude,
        lng: (this.addGrid.selectedBusiness.location && this.addGrid.selectedBusiness.location.lng) || this.addGrid.selectedBusiness.longitude
      };
      
      // Parse grid size (e.g., "3x3" -> 3)
      const gridDimension = parseInt(this.addGrid.gridSize.split('x')[0]);
      // Distance between pins (NOT radius) - this is the exact gap between adjacent pins
      const distanceBetweenPins = parseFloat(this.addGrid.radius);

      // Generate grid points
      const points = [];
      // Step is exactly the distance between pins
      const step = distanceBetweenPins;
      const halfGrid = (gridDimension - 1) / 2;

      for (let i = 0; i < gridDimension; i++) {
        for (let j = 0; j < gridDimension; j++) {
          const latOffset = (i - halfGrid) * step / 69; // 1 degree latitude ≈ 69 miles
          const lngOffset = (j - halfGrid) * step / (69 * Math.cos(center.lat * Math.PI / 180));

          points.push({
            lat: center.lat + latOffset,
            lng: center.lng + lngOffset
          });
        }
      }

      this.addGrid.gridPoints = points;
    },
    
    closeModalOnBackdrop(event, modalType) {
      if (event.target.classList.contains('modal')) {
        if (modalType === 'addGrid') {
          this.closeAddGridModal();
        }
      }
    },
  },
  async created() {
    // Load API keys status on component creation
    await Promise.all([
      this.$store.dispatch('apiKeys/fetchGoogleMapsKey'),
      this.$store.dispatch('apiKeys/fetchValueSerpKey')
    ]);
  },
  
  mounted() {
    this.fetchGridScans();
  }
}
</script>

<style scoped>
/* Font import */
@import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600&display=swap');

/* Main Container */
.listing-table-container {
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 2px 20px 0px rgba(62, 187, 128, 0.1);
  padding: 40px;
  font-family: 'Rubik', sans-serif;
}

/* Table Section Header */
.table-section-header {
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
}

.header-with-button {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.table-section-title {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #19233A;
  line-height: 38px;
  margin-bottom: 8px;
  margin-top: 0;
}

.table-section-description {
  color: #757B89;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

/* Header Controls */
.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  gap: 20px;
}

.search-filter-group {
  display: flex;
  gap: 20px;
  align-items: center;
}

.search-bar {
  position: relative;
  width: 400px;
  height: 50px;
}

.search-bar input {
  width: 100%;
  height: 100%;
  border: 1px solid rgba(25, 35, 58, 0.3);
  border-radius: 5px;
  padding: 13px 15px 13px 50px;
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  color: #19233A;
  background: #ffffff;
}

.search-bar input::placeholder {
  color: rgba(25, 35, 58, 0.4);
}

.search-bar .search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  opacity: 0.4;
}

.search-bar .search-icon svg {
  width: 100%;
  height: 100%;
  stroke: #19233A;
  fill: none;
}

.filter-button {
  width: 60px;
  height: 50px;
  border: 1px solid rgba(25, 35, 58, 0.3);
  border-radius: 5px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.filter-button svg {
  width: 30px;
  height: 30px;
  stroke: #757B89;
  fill: none;
}

.actions-dropdown {
  width: 200px;
  height: 50px;
  border: 1px solid rgba(25, 35, 58, 0.3);
  border-radius: 5px;
  background: #ffffff;
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #19233A;
}

.actions-dropdown .arrow-down svg {
  width: 24px;
  height: 24px;
  stroke: #19233A;
  fill: none;
}

/* Table Styles */
.table-container {
  margin-bottom: 20px;
  overflow-x: auto;
  border: 1px solid #E5E7EB;
  border-radius: 5px;
}

.table-header {
  min-height: 54px;
  background: #f8f9fc;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  padding: 0 20px;
  width: fit-content;
  min-width: 100%;
}

.table-row {
  min-height: 54px;
  background: #ffffff;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  padding: 10px 20px;
  width: fit-content;
  min-width: 100%;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row.special-state {
  justify-content: center;
  padding: 40px 20px;
  min-height: 100px;
}

.table-cell {
  display: flex;
  align-items: center;
  font-family: 'Rubik', sans-serif;
  font-size: 12px;
  color: #19233A;
  padding-right: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-header .table-cell {
  font-weight: 500;
  color: #111929;
}

/* Column Widths - Fixed widths for consistency */
.location-cell {
  width: 200px;
  min-width: 200px;
  flex-shrink: 0;
}

.search-query-cell {
  width: 180px;
  min-width: 180px;
  flex-shrink: 0;
}

.business-cell {
  width: 180px;
  min-width: 180px;
  flex-shrink: 0;
}

.search-settings-cell {
  width: 180px;
  min-width: 180px;
  flex-shrink: 0;
  display: flex !important;
  align-items: center;
  gap: 6px;
  padding-right: 8px;
}

.last-scan-cell {
  width: 140px;
  min-width: 140px;
  flex-shrink: 0;
}

.action-cell {
  width: 240px;
  min-width: 240px;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-right: 0;
}

/* Checkbox */
.checkbox {
  width: 16px;
  height: 16px;
  border: 1px solid #757B89;
  border-radius: 2px;
  background: transparent;
  cursor: pointer;
}

/* Icons */
.grid-icon, .miles-icon {
  width: 18px;
  height: 18px;
  opacity: 0.6;
  flex-shrink: 0;
}

.grid-icon svg, .miles-icon svg {
  width: 100%;
  height: 100%;
  fill: #19233A;
}

.search-settings-cell span {
  font-size: 13px;
  white-space: nowrap;
}

/* Sort Icon */
.sort-icon {
  margin-left: 8px;
  width: 14px;
  height: 14px;
}

.sort-icon svg {
  width: 100%;
  height: 100%;
  fill: #757B89;
}

/* Action Buttons */
.action-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: none;
  /* background: rgba(36, 119, 246, 0.08); */
  color: #2477F6;
  font-size: 12px;
  font-weight: 400;
  font-family: 'Rubik', sans-serif;
  border-radius: 8px;
  cursor: pointer;
  text-transform: capitalize;
  white-space: nowrap;
  color: #ef4444;
  border-radius: 6px;
  border-color: #ef4444;
  flex-shrink: 0;
  border: 1px solid;
}

.action-button:hover {
  background: rgba(36, 119, 246, 0.1);
}

.action-button svg {
  width: 18px;
  height: 18px;
  fill: #2477F6;
  flex-shrink: 0;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #757B89;
}

.pagination-dropdown {
  width: 80px;
  height: 38px;
  border: 1px solid #c9c9d4;
  border-radius: 5px;
  background: #ffffff;
  padding: 0 10px;
  cursor: pointer;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #19233A;
}
.keyword-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #e0e7ff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  max-width: 95%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.keywords-container {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-start;
  width: 100%;
}

.pagination-right {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  color: #757B89;
}

.pagination-button {
  width: 40px;
  height: 40px;
  border: 1px solid #c9c9d4;
  border-radius: 5px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.pagination-button:hover {
  background: #f8f9fc;
}

.pagination-button svg {
  width: 20px;
  height: 20px;
  fill: #757B89;
}

/* Responsive Design */
@media (max-width: 1400px) {
  .listing-table-container {
    padding: 20px;
  }
  
  .search-bar {
    width: 300px;
  }
  
  .actions-dropdown {
    width: 150px;
  }
}

@media (max-width: 768px) {
  .header-controls {
    flex-direction: column;
    gap: 16px;
  }
  
  .search-filter-group {
    width: 100%;
    flex-direction: column;
  }
  
  .search-bar {
    width: 100%;
  }
  
  .table-header, .table-row {
    font-size: 12px;
    padding: 0 10px;
  }
  
  .location-cell, .search-query-cell, .business-cell {
    width: 120px;
  }
  
  .search-settings-cell {
    width: 100px;
  }
  
  .last-scan-cell {
    width: 100px;
  }
  
  .action-cell {
    width: 180px;
  }
  
  .pagination {
    flex-direction: column;
    gap: 16px;
  }
}

/* Add Grid Button Styles */
.add-grid-btn {
  padding: 12px 20px;
  background: #FFFFFF;
  color: #374151;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.25rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.add-grid-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.5s ease;
}

.add-grid-btn:hover::before {
  left: 100%;
}

.add-grid-btn:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.add-grid-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.btn-icon {
  font-size: 16px;
  line-height: 1;
}

/* Modal Styles */
.modal {
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;  
  justify-content: flex-start; 
}

.modal-content {
  background: #FFFFFF;
  margin: 2% auto;
  padding: 32px;
  border-radius: 16px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 26px !important;
  border-bottom: 1px solid #E5E7EB;
}

.modal-title {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 1.25rem;
  color: #19233A;
}

.close-modal {
  background: #f9fafb;
  border: none;
  color: #757B89;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.25rem;
  transition: background 0.2s ease;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-modal:hover {
  background: #f3f4f6;
}

.modal-body {
  margin-bottom: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-family: 'Rubik', sans-serif;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #FFFFFF;
  color: #19233A;
  font-size: 0.875rem;
  font-family: 'Rubik', sans-serif;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.form-select {
  cursor: pointer;
}

.input-hint {
  font-size: 0.75rem;
  color: #757B89;
  margin-top: 4px;
  font-style: italic;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #E5E7EB;
}

.action-btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn.primary {
  background: #4f46e5  !important;
  color: white  !important;
}

.action-btn.primary:hover {
  background: #4338ca !important;
}

.action-btn.secondary {
  background: #FFFFFF !important;
  color: #374151 !important;
  border: 1px solid #E5E7EB !important;
}

.action-btn.secondary:hover {
  background: #E5E7EB;
}

/* Keyword & Email Tags Styles */
.keywords-input-container,
.competitors-input-container,
.email-tags-container {
  width: 100%;
}

.keywords-tag-container,
.competitors-tag-container,
.email-tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  min-height: 48px;
}

.keywords-tag-wrapper,
.competitors-tag-wrapper,
.email-tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.keyword-tag-item,
.competitor-tag-item,
.email-tag-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 20px;
  font-size: 0.875rem;
  color: #374151;
}

.keyword-tag-text,
.competitor-tag-text,
.email-tag-text {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.keyword-tag-text[contenteditable="true"] {
  outline: none;
  cursor: text;
}

.keyword-tag-text[contenteditable="true"]:focus {
  background: #F3F4F6;
  padding: 2px 4px;
  border-radius: 3px;
}

.keyword-tag-delete,
.competitor-tag-delete,
.email-tag-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: #F3F4F6;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s ease;
  padding: 0;
}

.keyword-tag-delete:hover,
.competitor-tag-delete:hover,
.email-tag-delete:hover {
  background: #E5E7EB;
}

.keyword-tag-delete svg,
.competitor-tag-delete svg,
.email-tag-delete svg {
  width: 10px;
  height: 10px;
  stroke: #757B89;
  stroke-width: 2;
}

.keyword-tag-input,
.competitor-search-input,
.email-tag-input {
  flex: 1;
  min-width: 150px;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.875rem;
  color: #374151;
}

.keyword-tag-input::placeholder,
.competitor-search-input::placeholder,
.email-tag-input::placeholder {
  color: #9CA3AF;
}

.email-tag-item.invalid {
  background: #FEE2E2;
  border-color: #FCA5A5;
}

/* Grid Preview Styles */
.grid-preview-container {
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 16px;
}

.grid-preview-map {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #E5E7EB;
}

.grid-info-badge {
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.map-hint {
  text-align: center;
  font-size: 0.75rem;
  color: #757B89;
  margin-top: 8px;
  font-style: italic;
}

/* Scheduling Section Styles */
.scheduling-section {
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 20px;
  margin-top: 24px;
}

.section-title {
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  color: #19233A;
  margin-bottom: 16px;
}

.notification-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin: 0;
}

.checkmark {
  display: none;
}
</style>